import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

const DISMISSED_KEY = "qrpts-pwa-dismissed";

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;
    // Detect standalone (already installed) — skip
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (standalone) return;

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    if (iOS) {
      // iOS doesn't fire beforeinstallprompt — show manual hint after 3s
      const t = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(t);
    }

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, "1");
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISSED_KEY, "1");
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-4 right-4 left-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[70]"
        data-testid="pwa-prompt"
      >
        <div className="glass-card rounded-2xl p-4 border border-blue-500/30" style={{ background: "rgba(16,18,24,0.96)" }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-white tracking-tight">Ana Ekrana Ekle</div>
              <div className="text-xs text-white/60 mt-1">
                QRPTS'yi telefonunuza uygulama gibi kurun — daha hızlı erişim.
              </div>
              {isIOS && showIOSHelp && (
                <div className="mt-3 text-xs text-white/80 space-y-1 bg-white/5 rounded-lg p-3">
                  <div>1. Safari alt menüde <span className="font-semibold">Paylaş</span> simgesine dokunun</div>
                  <div>2. <span className="font-semibold">Ana Ekrana Ekle</span> seçeneğini seçin</div>
                  <div>3. Sağ üstten <span className="font-semibold">Ekle</span>'ye basın</div>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                {isIOS ? (
                  <Button
                    size="sm"
                    onClick={() => setShowIOSHelp(true)}
                    className="bg-blue-600 hover:bg-blue-500 text-white"
                    data-testid="pwa-ios-help"
                  >
                    Nasıl?
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={install}
                    className="bg-blue-600 hover:bg-blue-500 text-white"
                    data-testid="pwa-install-btn"
                  >
                    Yükle
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={dismiss} className="text-white/60 hover:text-white" data-testid="pwa-dismiss">
                  Daha sonra
                </Button>
              </div>
            </div>
            <button onClick={dismiss} className="text-white/40 hover:text-white/80" data-testid="pwa-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
